import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import { setAnonIntercomStatus } from 'utils/intercomStatus';

const INTERCOM_SCRIPT_ID = 'kloow-intercom-widget-script';

function isDashboardRoute(pathname) {
  return String(pathname || '').startsWith('/main/dashboard');
}

function isLoginRoute(pathname) {
  return String(pathname || '').startsWith('/auth/login');
}

function shutdownIntercom() {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.Intercom === 'function') {
    try {
      window.Intercom('shutdown');
    } catch (error) {
      console.error('Failed to shut down Intercom:', error);
    }
  }

  delete window.intercomSettings;
}

function ensureIntercomScript(appId) {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const scriptUrl = `https://widget.intercom.io/widget/${appId}`;

  return new Promise((resolve, reject) => {
    let script = document.getElementById(INTERCOM_SCRIPT_ID);

    if (script) {
      const currentSrc = script.getAttribute('src') || '';

      if (currentSrc !== scriptUrl) {
        script.remove();
        script = null;
      }
    }

    if (script) {
      if (script.dataset.loaded === 'true') {
        resolve();
        return;
      }

      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener(
        'error',
        () => reject(new Error('Failed to load the Intercom widget script.')),
        { once: true }
      );
      return;
    }

    script = document.createElement('script');
    script.id = INTERCOM_SCRIPT_ID;
    script.async = true;
    script.src = scriptUrl;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load the Intercom widget script.'));
    document.head.appendChild(script);
  });
}

export default function IntercomMessenger() {
  const location = useLocation();
  const { axiosServices, isAuthenticated, isAuthReady } = useAuth();

  useEffect(() => {
    // Two distinct boot modes, one widget owner:
    //  - "authenticated": signed-in user on the dashboard — booted with a signed
    //    user JWT so Intercom ties the conversation to their account.
    //  - "anonymous": logged-out visitor on the login screen — booted as an
    //    anonymous lead (no JWT/identity) so they can reach live chat pre-auth.
    let bootMode = null;
    if (isAuthReady) {
      if (isAuthenticated && isDashboardRoute(location.pathname)) {
        bootMode = 'authenticated';
      } else if (!isAuthenticated && isLoginRoute(location.pathname)) {
        bootMode = 'anonymous';
      }
    }

    let cancelled = false;
    let refreshTimeoutId = null;

    if (!bootMode) {
      shutdownIntercom();
      return undefined;
    }

    const scheduleRefresh = (ttlSeconds) => {
      const normalizedTtlSeconds = Number(ttlSeconds);

      if (!Number.isFinite(normalizedTtlSeconds) || normalizedTtlSeconds <= 0) {
        return;
      }

      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }

      const refreshDelayMs = Math.max(normalizedTtlSeconds - 60, 60) * 1000;
      refreshTimeoutId = window.setTimeout(() => {
        void bootAuthenticated();
      }, refreshDelayMs);
    };

    const bootAuthenticated = async () => {
      try {
        const response = await axiosServices.get('/intercom/settings');
        const settings = response?.data?.enabled ? response.data.settings : null;

        if (cancelled) {
          return;
        }

        if (!settings?.app_id || !settings?.intercom_user_jwt) {
          shutdownIntercom();
          return;
        }

        const bootPayload = {
          app_id: settings.app_id,
          api_base: settings.api_base,
          intercom_user_jwt: settings.intercom_user_jwt,
          hide_default_launcher: false,
          horizontal_padding: 24,
          vertical_padding: 24,
          z_index: 1400
        };

        shutdownIntercom();
        window.intercomSettings = bootPayload;
        await ensureIntercomScript(settings.app_id);

        if (cancelled) {
          return;
        }

        if (typeof window.Intercom !== 'function') {
          throw new Error('Intercom did not initialize correctly.');
        }

        window.Intercom('boot', bootPayload);
        scheduleRefresh(settings.jwt_ttl_seconds);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to initialize Intercom:', error);
        shutdownIntercom();
      }
    };

    // Anonymous boot: no JWT, no identity, no refresh timer. The public endpoint
    // only ever returns the non-secret app_id + api_base, so the visitor opens
    // Intercom as an anonymous lead. We report the real boot OUTCOME via
    // setAnonIntercomStatus so the login screen shows its static "Help & support"
    // fallback whenever the launcher is NOT up — whether Intercom is disabled
    // (enabled:false), the request fails, OR a configured widget fails to load.
    const bootAnonymous = async () => {
      setAnonIntercomStatus('pending');

      try {
        const response = await axiosServices.get('/intercom/public-settings');
        const settings = response?.data?.enabled ? response.data.settings : null;

        if (cancelled) {
          return;
        }

        if (!settings?.app_id) {
          shutdownIntercom();
          setAnonIntercomStatus('unavailable');
          return;
        }

        const bootPayload = {
          app_id: settings.app_id,
          api_base: settings.api_base,
          hide_default_launcher: false,
          horizontal_padding: 24,
          vertical_padding: 24,
          z_index: 1400
        };

        shutdownIntercom();
        window.intercomSettings = bootPayload;
        await ensureIntercomScript(settings.app_id);

        if (cancelled) {
          return;
        }

        if (typeof window.Intercom !== 'function') {
          throw new Error('Intercom did not initialize correctly.');
        }

        window.Intercom('boot', bootPayload);
        setAnonIntercomStatus('active');
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to initialize anonymous Intercom:', error);
        shutdownIntercom();
        setAnonIntercomStatus('unavailable');
      }
    };

    if (bootMode === 'authenticated') {
      void bootAuthenticated();
    } else {
      void bootAnonymous();
    }

    return () => {
      cancelled = true;
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }
    };
  }, [axiosServices, isAuthenticated, isAuthReady, location.pathname]);

  useEffect(() => () => shutdownIntercom(), []);

  return null;
}
